<template>
  <fragment>
    <v-row>
      <v-col cols="12">
        <span>{{ $t("devices.type") }}:</span>
        <span class="ml-2 font-weight-bold">{{ deviceType }}</span>
        <span class="ml-8">{{ $t("devices.network") }}:</span>
        <span class="ml-2 font-weight-bold">{{ networkSSID }}</span>
      </v-col>
    </v-row>

    <wx-data-table
      :headers="computedDataSourceColumns"
      :items="computedDataSources"
      :items-per-page="1000"
      :filterable="true"
      :sort-by="['id', 'measureType']"
      :sort-desc="[false, false]"
      :no-results-text="this.$t('common.noMatchingRecords')"
      :hide-default-footer="true"
      mobile-breakpoint="sm"
      class="wx-panel mt-6"
    >
      <template #[`item.id`]="{ item }">
        <div>
          <div class="wx-typo-sm">
            {{ item.id }}
          </div>
          <div class="wx-typo-xs font-weight-light">
            {{ item.description }}
          </div>
        </div>
      </template>
      <template #[`item.role`]="{ item }">
        <div class="wx-typo-ss font-weight-light">
          {{ item.role }}
        </div>
      </template>
      <template #[`item.productionUnitNames`]="{ item }">
        <v-chip small outlined v-for="(name, index) in item.productionUnitNames" :key="`reason-${index}`" class="mr-1">
          {{ name }}
        </v-chip>
      </template>
      <template #[`item.isTimelineContributor`]="{ item }">
        <v-icon :color="item.isTimelineContributor ? 'success' : 'error'">
          {{ item.isTimelineContributor ? 'mdi-check' : 'mdi-close' }}
        </v-icon>
      </template>
      <template #[`item.isQuantityContributor`]="{ item }">
        <v-icon :color="item.isQuantityContributor ? 'success' : 'error'">
          {{ item.isQuantityContributor ? 'mdi-check' : 'mdi-close' }}
        </v-icon>
      </template>
      <template #no-data>
        {{ $t("devices.noDataSources") }}
      </template>
    </wx-data-table>

    <wx-data-table
      :headers="computedLastMeasureColumns"
      :items="computedLastMeasures"
      :items-per-page="1000"
      :filterable="true"
      :sort-by="['id', 'measureType']"
      :sort-desc="[false, false]"
      :no-results-text="this.$t('common.noMatchingRecords')"
      :hide-default-footer="true"
      mobile-breakpoint="sm"
      class="wx-panel mt-10 mb-3"
    >
      <template #[`item.dataSourceId`]="{ item }">
        <div>
          <div class="wx-typo-sm">
            {{ item.dataSourceId }}
          </div>
          <div class="wx-typo-xs font-weight-light">
            {{ item.dataSourceDescription }}
          </div>
        </div>
      </template>
      <template #[`item.dataSourceRole`]="{ item }">
        <div class="wx-typo-sm font-weight-light">
          {{ item.dataSourceRole }}
        </div>
      </template>
      <template #[`item.lastReceptionDateTime`]="{ item }">
        <span :class="getDateTimeCssClasses(item.lastReceptionTimestamp)">{{ item.lastReceptionDateTime }}</span>
      </template>
      <template #[`item.lastMeasureDateTime`]="{ item }">
        <span :class="getDateTimeCssClasses(item.lastMeasureTimestamp)">{{ item.lastMeasureDateTime }}</span>
      </template>
      <template #no-data>
        {{ $t("devices.noDataSources") }}
      </template>
    </wx-data-table>

    <div class="ml-4">
      <span class="wx-typo-sm font-weight-light">
        {{ $t("devices.status.lastCheck", { date: lastRefreshDate }) }}
      </span>
    </div>
    <div class="ml-4">
      <span class="wx-typo-sm font-weight-light">
        {{ $t("devices.status.timezone", { timezone: factoryTimezone }) }}
      </span>
    </div>

    <v-row>
      <v-col cols="12" class="mt-4">
        <wx-btn-standard color="primary" class="my-3" @click="toDevices()">
          <v-icon class="mr-2">mdi-arrow-left</v-icon>
          {{ $t("common.back") }}
        </wx-btn-standard>
      </v-col>
    </v-row>
  </fragment>
</template>

<script>
import * as TimeUtils from "@/store/TimeUtils";
import { DateTime } from "luxon";
import { DATE_TIME_FORMAT_NO_TIMEZONE } from "@/store/TimeUtils";
import { mapGetters } from "vuex";
import DataSourceService from "@/components/devices/DataSourceService";
import ErrorHandling from "@/components/ErrorHandling";
import ProductionUnitService from "@/components/productionunit/ProductionUnitService";
import WxDataTable from "@/components/ui/WxDataTable.vue";
import WxBtnStandard from "@/components/ui/WxBtnStandard.vue";
import RouteService from "@/router/RouteService";
import DeviceService from "@/components/devices/DeviceService";

export default {
  name: "DeviceStatus",
  components: { WxBtnStandard, WxDataTable },
  props: {
    deviceId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      deviceType: "",
      networkSSID: "",
      dataSourcesActivity: [],
      refreshLoop: null,
      lastRefreshDate: "",
    };
  },
  computed: {
    ...mapGetters("devicesAdmin", ["activeFactoryDataSources"]),
    ...mapGetters("navigation", ["activeFactory", "activeFactoryProductionUnits"]),
    computedDataSourceColumns() {
      return [
        { text: this.$t("devices.status.dataSourceId"), class: "col-ds", align: "start", value: "id" },
        { text: this.$t("devices.status.dataSourceRole"), class: "col-role", align: "start", value: "role" },
        {
          text: this.$t("devices.status.measureType"),
          class: "col-measure-type",
          align: "start",
          value: "measureType",
        },
        { text: this.$t("devices.status.unit"), class: "col-unit", align: "start", value: "unit" },
        { text: this.$t("devices.productionUnits"), class: "col-pu", value: "productionUnitNames" },
        { text: this.$t("devices.status.isTimelineContributor"), class: "col-pu", value: "isTimelineContributor" },
        { text: this.$t("devices.status.isQuantityContributor"), class: "col-pu", value: "isQuantityContributor" },
        { text: "", class: "col-actions", value: "actions", sortable: false },
      ];
    },
    computedDataSources() {
      let dataSources = this.activeFactoryDataSources.filter((ds) => ds.device_id === this.deviceId);
      return dataSources.map((ds) => {
        let productionUnitNames = [];
        ds.production_unit_ids.forEach((puId) => {
          const pu = this.activeFactoryProductionUnits.find((pu) => pu.id === puId);
          if (pu) {
            if (!productionUnitNames.includes(pu.name)) {
              productionUnitNames.push(pu.name);
            }
          }
        });
        return {
          id: ds.id,
          role: ds.role ? ds.role.toUpperCase() : "",
          description: ds.description ? ds.description : "",
          measureType: ds.measure_type,
          unit: ds.unit,
          productionUnitNames: productionUnitNames,
          isTimelineContributor: ds.is_timeline_contributor,
          isQuantityContributor: ds.is_quantity_contributor,
        };
      });
    },
    computedLastMeasureColumns() {
      return [
        { text: this.$t("devices.status.dataSourceId"), class: "col-ds", align: "start", value: "dataSourceId" },
        { text: this.$t("devices.status.dataSourceRole"), class: "col-role", align: "start", value: "dataSourceRole" },
        {
          text: this.$t("devices.status.lastReceptionTime"),
          class: "col-last-reception-time",
          align: "start",
          value: "lastReceptionDateTime",
        },
        {
          text: this.$t("devices.status.lastMeasureTimestamp"),
          class: "col-last-timestamp",
          align: "start",
          value: "lastMeasureDateTime",
        },
        { text: this.$t("devices.status.lastMeasureValue"), class: "col-measure-value", value: "lastMeasureValue" },
      ];
    },
    computedLastMeasures() {
      if (this.activeFactory) {
        return this.dataSourcesActivity.map((dsa) => {

          let dataSourceRole = "";
          let dataSourceDescription = "";
          let dataSources = this.activeFactoryDataSources.filter((ds) => ds.device_id === this.deviceId);
          let foundDatasource = dataSources.find((dataSource) => dataSource.id === dsa.id);
          if (foundDatasource) {
            dataSourceRole = foundDatasource.role ? foundDatasource.role.toUpperCase() : "";
            dataSourceDescription = foundDatasource.description ? foundDatasource.description : "";
          }
          return {
            dataSourceId: dsa.id,
            dataSourceRole: dataSourceRole,
            dataSourceDescription: dataSourceDescription,
            lastReceptionTimestamp: dsa.last_reception_time,
            lastReceptionDateTime: this.formatTime(dsa.last_reception_time, this.activeFactory.timezone),
            lastMeasureTimestamp: dsa.last_reception_time,
            lastMeasureDateTime: this.formatTime(dsa.last_measure_timestamp, this.activeFactory.timezone),
            lastMeasureValue:
              dsa.last_measure_value !== null && dsa.last_measure_value !== undefined ? dsa.last_measure_value : "-",
          };
        });
      }
      return [];
    },
    factoryTimezone() {
      if (this.activeFactory) {
        return this.activeFactory.timezone;
      }
      return "";
    },
  },
  methods: {
    toDevices() {
      this.$router.replace(RouteService.toDevices());
    },
    loadDataSourcesActivity() {
      DataSourceService.getDataSourcesActivity(this.deviceId)
        .then((httpResponse) => this.handleRetrievalResponse(httpResponse))
        .catch((error) => this.handleRetrievalError(error));
    },
    handleRetrievalResponse(httpResponse) {
      if (httpResponse.status === 200) {
        this.dataSourcesActivity = httpResponse.data;
        this.lastRefreshDate = this.formatTime(DateTime.now().toMillis(), this.activeFactory.timezone);
      }
    },
    handleRetrievalError(httpResponse) {
      this.showOperationError(ErrorHandling.buildErrorsMessages(httpResponse, this.getErrorMessage));
    },
    loadDeviceNetwork() {
      DeviceService.getNetworkInfo(this.deviceId)
        .then((httpResponse) => {
          if (httpResponse.status === 200) {
            this.deviceType = httpResponse.data.device_type;
            this.networkSSID = httpResponse.data.ssid ? httpResponse.data.ssid : "";
          }
        })
        .catch(() => {
          this.deviceType = "";
          this.networkSSID = "-";
        });
    },
    getErrorMessage(code) {
      return this.$t("common.errors.default", { code: code });
    },
    getRefreshDelay() {
      return 10000; // 10 seconds
    },
    formatTime(timeMillis, timezone) {
      if (timeMillis) {
        return TimeUtils.fromEpochMillis(timeMillis, timezone, DATE_TIME_FORMAT_NO_TIMEZONE);
      } else {
        return "-";
      }
    },
    getDateTimeCssClasses(timestamp) {
      if (timestamp) {
        const diff = DateTime.utc().toMillis() - timestamp;
        if (diff > ProductionUnitService.getTimeWithoutMeasureToleranceMillis()) {
          return "error--text";
        }
        return "success--text";
      }
      return "error--text";
    },
    loadDeviceData() {
      this.loadDeviceNetwork();
      this.loadDataSourcesActivity();
    },
  },
  mounted() {
    clearInterval(this.refreshLoop);
    this.loadDeviceData();
    this.refreshLoop = setInterval(() => {
      this.loadDeviceData();
    }, this.getRefreshDelay());
  },
  beforeDestroy() {
    clearInterval(this.refreshLoop);
  },
};
</script>

<style lang="scss" scoped>
.action-container {
  display: flex;
  margin-bottom: 20px;
  justify-content: space-between;

  .v-input {
    max-width: 400px;
  }
}
::v-deep .col-ds {
  width: 25%;
}

::v-deep .col-role {
  width: 10%;
}

::v-deep .col-measure-type {
  width: 15%;
}

::v-deep .col-unit {
  width: 15%;
}

::v-deep .col-last-reception-time {
  width: 20%;
}

::v-deep .col-last-timestamp {
  width: 25%;
}

::v-deep .col-measure-value {
  width: 20%;
}
</style>
